import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import CustomPagination from '../../../components/forms/Pagination';
import { UserContext } from '../../../components/shared/useUser';
import { Plus } from '../../../assets';
import {
  IssueControlArea,
  IssueException,
  IssueSeverity,
  IssueStatus,
  IssueType,
  issuesExceptionsSeed,
} from '../../../mocks/data/issuesExceptionsData';

const QuickFilterDivider = () => (
  <div style={{ width: 1, height: 24, backgroundColor: '#E0E0E0', margin: '0 8px' }} />
);

const severityColor: Record<IssueSeverity, 'default' | 'warning' | 'error' | 'info'> = {
  Low: 'default',
  Medium: 'info',
  High: 'warning',
  Critical: 'error',
};

const controlAreas: IssueControlArea[] = [
  'Stock', 'Ordering', 'Labour', 'Food Safety', 'Allergens', 'Nutrition',
  'Waste', 'Quality', 'Pricing', 'Hospitality', 'Events', 'Customer Experience',
];

const issueTypes: IssueType[] = [
  'Food temperature check failed', 'Allergen label missing', 'Waste over threshold',
  'Stock variance outside tolerance', 'Product unavailable', 'Pricing mismatch',
  'Hospitality order missed', 'Training expired', 'Customer complaint raised',
  'Cleaning check missed', 'Equipment compliance overdue',
];

const statuses: IssueStatus[] = ['Open', 'In Progress', 'Escalated', 'Resolved', 'Closed'];
const severities: IssueSeverity[] = ['Low', 'Medium', 'High', 'Critical'];

const emptyForm: Omit<IssueException, 'id'> = {
  site: '',
  controlArea: 'Food Safety',
  type: 'Food temperature check failed',
  severity: 'Medium',
  status: 'Open',
  owner: '',
  dueDate: '',
  sourceRecord: '',
  impact: '',
  requiresActionItem: false,
  blocksService: false,
};

const IssuesExceptionsPage: React.FC = () => {
  const { t } = useTranslation('complianceControls');
  const { hasPermissionTo } = useContext(UserContext);

  const canView = hasPermissionTo ? hasPermissionTo(['ActionItemsView']) : true;
  // lazy: demo CRUD permission object mirrors the shape a real permission model would provide
  // (canCreate/canRead/canUpdate/canDelete) so wiring real permissions later is a drop-in.
  const permissions = {
    canCreate: hasPermissionTo ? hasPermissionTo(['ActionItemsView']) : true,
    canRead: canView,
    canUpdate: hasPermissionTo ? hasPermissionTo(['ActionItemsView']) : true,
    canDelete: hasPermissionTo ? hasPermissionTo(['ActionItemsView']) : true,
  };

  const [issues, setIssues] = useState<IssueException[]>(issuesExceptionsSeed);
  const [statusFilter, setStatusFilter] = useState<IssueStatus | ''>('');
  const [severityFilter, setSeverityFilter] = useState<IssueSeverity | ''>('');
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [advControlArea, setAdvControlArea] = useState<IssueControlArea | ''>('');
  const [advType, setAdvType] = useState<IssueType | ''>('');
  const [advSite, setAdvSite] = useState('');
  const [advOwner, setAdvOwner] = useState('');
  const [advDateFrom, setAdvDateFrom] = useState('');
  const [advDateTo, setAdvDateTo] = useState('');

  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<IssueException, 'id'>>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<IssueException | null>(null);

  const handleClearFilters = () => {
    setStatusFilter('');
    setSeverityFilter('');
    setAdvControlArea('');
    setAdvType('');
    setAdvSite('');
    setAdvOwner('');
    setAdvDateFrom('');
    setAdvDateTo('');
    setPage(0);
  };

  const filteredIssues = useMemo(() => {
    return issues.filter((issue) => {
      if (statusFilter && issue.status !== statusFilter) return false;
      if (severityFilter && issue.severity !== severityFilter) return false;
      if (advControlArea && issue.controlArea !== advControlArea) return false;
      if (advType && issue.type !== advType) return false;
      if (advSite && !issue.site.toLowerCase().includes(advSite.toLowerCase())) return false;
      if (advOwner && !issue.owner.toLowerCase().includes(advOwner.toLowerCase())) return false;
      if (advDateFrom && issue.dueDate < advDateFrom) return false;
      if (advDateTo && issue.dueDate > advDateTo) return false;
      return true;
    });
  }, [issues, statusFilter, severityFilter, advControlArea, advType, advSite, advOwner, advDateFrom, advDateTo]);

  const maxPages = Math.max(1, Math.ceil(filteredIssues.length / rowsPerPage));
  const pagedIssues = filteredIssues.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleOpenCreate = () => {
    if (!permissions.canCreate) return;
    setEditingId(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const handleOpenEdit = (issue: IssueException) => {
    if (!permissions.canUpdate) return;
    setEditingId(issue.id);
    const { id: _id, ...rest } = issue;
    setFormData(rest);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleSave = () => {
    if (editingId) {
      setIssues((prev) => prev.map((issue) => (issue.id === editingId ? { ...formData, id: editingId } : issue)));
    } else {
      const newId = `ISS-${Math.floor(1000 + Math.random() * 9000)}`;
      setIssues((prev) => [{ ...formData, id: newId }, ...prev]);
    }
    handleCloseDialog();
  };

  const handleRequestDelete = (issue: IssueException, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!permissions.canDelete) return;
    setDeleteTarget(issue);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    setIssues((prev) => prev.filter((issue) => issue.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  if (!permissions.canRead) {
    return (
      <PageArea>
        <PageTitle>{t('issuesExceptions.title')}</PageTitle>
        <Pill text={t('noPermissionToView')} />
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}>
          <Grid container
            justifyContent="space-between"
            alignItems="flex-start">
            <Grid item>
              <PageTitle subTitle={t('issuesExceptions.subtitle')}>
                {t('issuesExceptions.title')}
              </PageTitle>
            </Grid>
            <Grid item>
              {permissions.canCreate && (
                <Button
                  size="lg"
                  variant="primary"
                  startIcon={<Plus />}
                  onClick={handleOpenCreate}
                >
                  {t('issuesExceptions.createIssue')}
                </Button>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}>
          <Grid container
            spacing={1}
            alignItems="center">
            <Grid item>
              <Grid container
                spacing={1}
                alignItems="center">
                {statuses.map((s) => (
                  <Grid item
                    key={s}>
                    <Chip
                      label={t(`statusValues.${s}`)}
                      color={statusFilter === s ? 'primary' : 'default'}
                      onClick={() => { setStatusFilter((prev) => (prev === s ? '' : s)); setPage(0); }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item>
              <QuickFilterDivider />
            </Grid>
            <Grid item>
              <Grid container
                spacing={1}
                alignItems="center">
                {severities.map((s) => (
                  <Grid item
                    key={s}>
                    <Chip
                      label={t(`severityValues.${s}`)}
                      color={severityFilter === s ? 'primary' : 'default'}
                      onClick={() => { setSeverityFilter((prev) => (prev === s ? '' : s)); setPage(0); }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}>
          <Accordion
            variant="borderless"
            expanded={isAdvancedOpen}
            onChange={(_, expanded) => setIsAdvancedOpen(expanded)}
          >
            <AccordionSummary>
              <Grid container
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '100%' }}>
                <Grid item>
                  <Grid container
                    spacing={1}
                    alignItems="center">
                    <Grid item>
                      {t('advancedFilters')}
                    </Grid>
                    <Grid item
                      style={{ marginTop: '8px' }}>
                      {isAdvancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Button
                    size="md"
                    variant="secondary"
                    onClick={handleClearFilters}
                  >
                    {t('clearFilters')}
                  </Button>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container
                spacing={3}>
                <Grid item
                  lg={3}
                  sm={6}
                  xs={12}>
                  <TextField
                    select
                    label={t('controlArea')}
                    value={advControlArea}
                    onChange={(e) => { setAdvControlArea(e.target.value as IssueControlArea | ''); setPage(0); }}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">{t('all')}</MenuItem>
                    {controlAreas.map((ca) => (
                      <MenuItem key={ca}
                        value={ca}>{t(`issuesExceptions.controlAreaValues.${ca}`)}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item
                  lg={3}
                  sm={6}
                  xs={12}>
                  <TextField
                    select
                    label={t('type')}
                    value={advType}
                    onChange={(e) => { setAdvType(e.target.value as IssueType | ''); setPage(0); }}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">{t('all')}</MenuItem>
                    {issueTypes.map((it) => (
                      <MenuItem key={it}
                        value={it}>{t(`issuesExceptions.typeValues.${it}`)}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item
                  lg={3}
                  sm={6}
                  xs={12}>
                  <TextField
                    label={t('site')}
                    value={advSite}
                    onChange={(e) => { setAdvSite(e.target.value); setPage(0); }}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item
                  lg={3}
                  sm={6}
                  xs={12}>
                  <TextField
                    label={t('owner')}
                    value={advOwner}
                    onChange={(e) => { setAdvOwner(e.target.value); setPage(0); }}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item
                  lg={3}
                  sm={6}
                  xs={12}>
                  <TextField
                    type="date"
                    label={t('dueDate')}
                    value={advDateFrom}
                    onChange={(e) => { setAdvDateFrom(e.target.value); setPage(0); }}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item
                  lg={3}
                  sm={6}
                  xs={12}>
                  <TextField
                    type="date"
                    label={t('dueDate')}
                    value={advDateTo}
                    onChange={(e) => { setAdvDateTo(e.target.value); setPage(0); }}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item
          xs={12}>
          <Section>
            <TableHeader>
              {t('issuesExceptions.title')}
              <Pill text={`${filteredIssues.length} ${t('issuesExceptions.items')}`} />
            </TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('site')}</TableCell>
                    <TableCell>{t('controlArea')}</TableCell>
                    <TableCell>{t('type')}</TableCell>
                    <TableCell>{t('severity')}</TableCell>
                    <TableCell>{t('status')}</TableCell>
                    <TableCell>{t('owner')}</TableCell>
                    <TableCell>{t('dueDate')}</TableCell>
                    <TableCell align="right">{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedIssues.map((issue) => (
                    <TableRow key={issue.id}
                      hover>
                      <TableCell>{issue.site}</TableCell>
                      <TableCell>{t(`issuesExceptions.controlAreaValues.${issue.controlArea}`)}</TableCell>
                      <TableCell>{t(`issuesExceptions.typeValues.${issue.type}`)}</TableCell>
                      <TableCell>
                        <Chip label={t(`severityValues.${issue.severity}`)}
                          size="small"
                          color={severityColor[issue.severity]} />
                      </TableCell>
                      <TableCell>{t(`statusValues.${issue.status}`)}</TableCell>
                      <TableCell>{issue.owner}</TableCell>
                      <TableCell>{issue.dueDate}</TableCell>
                      <TableCell align="right">
                        {permissions.canUpdate && (
                          <Tooltip title={t('edit') as string}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(issue)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {permissions.canDelete && (
                          <Tooltip title={t('delete') as string}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => handleRequestDelete(issue, e)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {pagedIssues.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8}
                        style={{ textAlign: 'center' }}>
                        {t('issuesExceptions.noResults')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </Section>
        </Grid>
      </Grid>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingId ? t('issuesExceptions.editIssue') : t('issuesExceptions.createIssue')}</DialogTitle>
        <DialogContent>
          <Grid container
            spacing={3}
            sx={{ mt: 0.5 }}>
            <Grid item
              xs={12}
              sm={6}>
              <TextField
                label={t('site')}
                value={formData.site}
                onChange={(e) => setFormData((prev) => ({ ...prev, site: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}
              sm={6}>
              <TextField
                select
                label={t('controlArea')}
                value={formData.controlArea}
                onChange={(e) => setFormData((prev) => ({ ...prev, controlArea: e.target.value as IssueControlArea }))}
                fullWidth
                size="small"
              >
                {controlAreas.map((ca) => (
                  <MenuItem key={ca}
                    value={ca}>{t(`issuesExceptions.controlAreaValues.${ca}`)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={12}
              sm={6}>
              <TextField
                select
                label={t('type')}
                value={formData.type}
                onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value as IssueType }))}
                fullWidth
                size="small"
              >
                {issueTypes.map((it) => (
                  <MenuItem key={it}
                    value={it}>{t(`issuesExceptions.typeValues.${it}`)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={12}
              sm={6}>
              <TextField
                select
                label={t('severity')}
                value={formData.severity}
                onChange={(e) => setFormData((prev) => ({ ...prev, severity: e.target.value as IssueSeverity }))}
                fullWidth
                size="small"
              >
                {severities.map((s) => (
                  <MenuItem key={s}
                    value={s}>{t(`severityValues.${s}`)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={12}
              sm={6}>
              <TextField
                select
                label={t('status')}
                value={formData.status}
                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as IssueStatus }))}
                fullWidth
                size="small"
              >
                {statuses.map((s) => (
                  <MenuItem key={s}
                    value={s}>{t(`statusValues.${s}`)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={12}
              sm={6}>
              <TextField
                label={t('owner')}
                value={formData.owner}
                onChange={(e) => setFormData((prev) => ({ ...prev, owner: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}
              sm={6}>
              <TextField
                type="date"
                label={t('dueDate')}
                value={formData.dueDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, dueDate: e.target.value }))}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item
              xs={12}
              sm={6}>
              <TextField
                label={t('issuesExceptions.sourceRecord')}
                value={formData.sourceRecord}
                onChange={(e) => setFormData((prev) => ({ ...prev, sourceRecord: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                label={t('issuesExceptions.impact')}
                value={formData.impact}
                onChange={(e) => setFormData((prev) => ({ ...prev, impact: e.target.value }))}
                fullWidth
                multiline
                minRows={2}
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}
              sm={6}>
              <TextField
                select
                label={t('issuesExceptions.requiresActionItem')}
                value={formData.requiresActionItem ? 'yes' : 'no'}
                onChange={(e) => setFormData((prev) => ({ ...prev, requiresActionItem: e.target.value === 'yes' }))}
                fullWidth
                size="small"
              >
                <MenuItem value="yes">{t('yes')}</MenuItem>
                <MenuItem value="no">{t('no')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item
              xs={12}
              sm={6}>
              <TextField
                select
                label={t('issuesExceptions.blocksService')}
                value={formData.blocksService ? 'yes' : 'no'}
                onChange={(e) => setFormData((prev) => ({ ...prev, blocksService: e.target.value === 'yes' }))}
                fullWidth
                size="small"
              >
                <MenuItem value="yes">{t('yes')}</MenuItem>
                <MenuItem value="no">{t('no')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}
            variant="secondary"
            size="md">
            {t('cancel')}
          </Button>
          <Button onClick={handleSave}
            variant="primary"
            size="md">
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>{t('delete')}?</DialogTitle>
        <DialogContent>{t('issuesExceptions.deleteIssueConfirm')}</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}
            variant="secondary"
            size="md">
            {t('cancel')}
          </Button>
          <Button onClick={handleConfirmDelete}
            variant="bad"
            size="md">
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default IssuesExceptionsPage;
